import { LOADING_STATUS } from '@sonifi/wb-core/common/Constants';
import apiService from '@sonifi/wb-core/services/api.service';
import { ENDPOINTS } from '@sonifi/wb-core/services/endpointsConstants';
import { PRODUCTS_KEYS } from '@sonifi/wb-products/constants/wbProductsConstants';
import {
  getProductById,
  getProductsSettings,
  getSettingByProduct,
} from '@sonifi/wb-products/services/products.service';
import {
  IRD_OPTIONS_TREE_KEYS,
  checkUnpublishedChanges,
  setIrdMenu,
  setIrdShowModal,
  setLoadingIrdMenu,
  setMenu,
  setNodePreviewSelected,
  setNodeSelected,
  setOptionSetDependencies,
  setPublishingStatus,
  setSavingForm,
} from '../../store/menu/irdMenuSlice';
import {
  getJobsPublishStatus,
  processLatestPublishJob,
  saveCSVJob,
} from './irdMenuJobs.service';
import { getIdFromUrl } from '@sonifi/wb-core/utils/parseHelper';
import { initIrdSettings } from '@sonifi/wb-in-room-dining/Settings/services/irdSettings.service';
import { getBodyIrdMenu } from './helpers/irdMenuRequestsHelper';
import {
  getInicialNode,
  getIrdMenuMapped,
  getIrdOptionsSetsDependenciesFromMenu,
} from './helpers/irdMenuHelper';
import {
  addNodeToWbTree,
  addWarnings,
  buildWbTreeByNodeUri,
  findNodeByUri,
  findNodeParent,
  removeNodeFromWbTree,
} from '@sonifi/wb-tree/utils/helper';
import escape from 'lodash/escape';
import { verifyIrdMenuStructure } from './helpers/irdNodeValidations';
import { setAlert } from '@sonifi/wb-notifier/store/notifierSlice';
import { initConfirm } from '@sonifi/wb-notifier/services/confirm.service';
import { uniqueId } from 'lodash';
import {
  processFiles,
  uploadFiles,
} from '@sonifi/wb-widget/services/file/file.service';
import { ALERTS_IDS } from '@sonifi/wb-notifier/constants/notifierConstants';
import { getBrandingBackgroundImage } from '@sonifi/wb-branding/services/branding.service';
import {
  getMediaDependencies,
  getOptionSetsDependencies,
} from './irdDependencies.service';
import { getIrdItems } from './irdItems.service';
import { GA_ACTIONS, GA_LABELS, sendEventGA } from '@sonifi/wb-core/services/ga.service';

const getIrdMenu = async (group, revenueCenter) => {
  try {
    const url = `${ENDPOINTS.IRD_MENUS}?group=${group.uri}`;
    const res = await apiService.get(url);
    const irdMenuData = res.data;
    const imgData = Object.keys(irdMenuData._embedded).reduce(
      (acc, resource) => {
        acc = irdMenuData._embedded[resource]?.image
          ? acc.concat(irdMenuData._embedded[resource].image)
          : acc;
        return acc;
      },
      [],
    );

    const mediaDependencies = await getMediaDependencies({
      group,
      imgData,
    });

    const irdMenu = getIrdMenuMapped({
      data: irdMenuData,
      group,
      mediaDependencies,
      revenueCenterUri: revenueCenter.uri,
    });

    return {
      ...irdMenu,
      menu: {
        ...irdMenu.menu,
        isRoot: true,
        subject: {
          ...irdMenu.menu.subject,
          title: revenueCenter?.title
            ? revenueCenter.title
            : irdMenu.menu.subject.title,
        },
      },
    };
  } catch (error) {
    return Promise.reject(error);
  }
};

export const initIrdMenu =
  ({ groupId, id, platform, revenueCenter }) =>
  async (dispatch, getState) => {
    dispatch(setLoadingIrdMenu(LOADING_STATUS.loading));
    try {
      const {
        groups: { selectedGroup },
      } = getState();
      // This dispatch also update products settings
      await dispatch(initIrdSettings({ groupId, platform }));
      const productsSettings = await dispatch(getProductsSettings(groupId));
      const irdMenu = await getIrdMenu(selectedGroup, revenueCenter);
      const optionSets = getIrdOptionsSetsDependenciesFromMenu(irdMenu.menu);
      const optionSetsDependencies = await getOptionSetsDependencies(
        optionSets,
      );
      const irdProductSettings = await dispatch(
        getSettingByProduct({
          groupId,
          productKey: PRODUCTS_KEYS.IRD,
        }),
      );
      const background = dispatch(
        getBrandingBackgroundImage({ groupId, productsSettings }),
      );
      const ird = dispatch(getProductById(PRODUCTS_KEYS.IRD));
      const { latestPublishJob, lastPublished, lastPublishUser } =
        await dispatch(getJobsPublishStatus({ irdMenuUri: irdMenu.uri }));
      const node = getInicialNode({ id, menu: irdMenu.menu });

      await Promise.all([
        dispatch(setOptionSetDependencies({ optionSetsDependencies })),
        dispatch(
          setMenu({
            background,
            id,
            ird,
            irdMenu,
            lastPublished,
            lastPublishUser,
            latestPublishJob,
            platform,
            productSettings: irdProductSettings,
          }),
        ),
        dispatch(setNodePreviewSelected({ node })),
        dispatch(canPublishMenu({ superStrict: false })),
        dispatch(setLoadingIrdMenu(LOADING_STATUS.success)),
      ]);

      return irdMenu.menu.id;
    } catch (error) {
      dispatch(setLoadingIrdMenu(LOADING_STATUS.error));
      console.error(error);
    }
  };

export const getImageAndUpdateNode = (node) => async (dispatch) => {
  try {
    const imageUrl = node.subject.image?.length
      ? node.subject.image[0].image
      : null;
    if (!imageUrl) {
      return false;
    }
    const imageId = getIdFromUrl({ url: imageUrl, parseToInt: false });
    const res = await apiService.get(`${ENDPOINTS.IMAGES}/${imageId}`);
    const fileUri = res.data._embedded[res.data.files[0]].file_uri;
    const nodeWithImage = {
      ...node,
      subject: {
        ...node.subject,
        image: [
          {
            ...node.subject.image[0],
            file_uri: fileUri,
            _embedded: res.data._embedded[res.data.files[0]],
          },
        ],
      },
    };
    dispatch(setNodeSelected({ node: nodeWithImage }));
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const toggleNodeActive = (node) => async (dispatch, getState) => {
  try {
    const {
      irdMenu: { irdMenu, nodePreviewSelected },
    } = getState();

    // Update Store first
    const irdMenuUpdated = buildWbTreeByNodeUri({ wbTree: irdMenu, node });
    const nodePreviewUpdated = findNodeByUri({
      menu: irdMenuUpdated.menu,
      nodeUri: nodePreviewSelected.subject.uri,
    });
    const bodyIrdMenu = getBodyIrdMenu({
      irdMenu: irdMenuUpdated,
    });

    const resMenu = await apiService.patch(
      `${ENDPOINTS.IRD_MENUS}/${irdMenu.id}`,
      bodyIrdMenu,
    );

    await Promise.all([
      dispatch(
        setIrdMenu({
          irdMenu: {
            ...irdMenuUpdated,
            last_updated: resMenu.data.last_updated,
          },
        }),
      ),
      dispatch(setNodePreviewSelected({ node: nodePreviewUpdated })),
      dispatch(checkUnpublishedChanges()),
    ]);
  } catch (error) {
    console.error(error);
  }
};

export const submitCategoryForm =
  ({ form, keepModalOpen, revenueCenterId, removeChildren = false }) =>
  async (dispatch, getState) => {
    dispatch(setSavingForm(true));
    try {
      const {
        irdMenu: { addNodeFrom, items, irdMenu },
        groups: { selectedGroup },
      } = getState();

      let node = {
        ...form.node,
      };

      const irdCategoriesBody = {
        availability_end: node.subject.availability_end,
        availability_start: node.subject.availability_start,
        delivery_end: node.subject.delivery_end,
        delivery_increment: node.subject.delivery_increment,
        delivery_start: node.subject.delivery_start,
        description: node.subject.description,
        group: selectedGroup.uri,
        id: node.subject.id,
        revenue_center: node.subject.revenue_center,
        title: node.subject.title,
        uri: node.subject.uri,
      };

      let irdMenuUpdated = {};

      if (node.id) {
        const res = await apiService.patch(
          `${ENDPOINTS.IRD_CATEGORIES}/${node.subject.id}`,
          irdCategoriesBody,
        );

        node = {
          ...node,
          children: removeChildren ? [] : node?.children || [],
          subject: {
            ...node.subject,
            ...res.data,
          },
          valid_child_types: [form.valid_child_types],
        };
        irdMenuUpdated = buildWbTreeByNodeUri({
          wbTree: irdMenu,
          node,
        });
      } else {
        const res = await apiService.post(
          ENDPOINTS.IRD_CATEGORIES,
          irdCategoriesBody,
        );
        node = {
          ...node,
          id: uniqueId(`${Date.now()}_`),
          active: true,
          subject: {
            ...res.data,
            ...node.subject,
          },
          indicatorImage: {
            showIcon: false,
            showTooltip: false,
          },
          secondaryValue: '',
          valid_child_types: [form.valid_child_types],
        };
        irdMenuUpdated = addNodeToWbTree({
          addNodeFrom,
          node,
          wbTree: irdMenu,
        });
      }

      const irdMenuBody = getBodyIrdMenu({ irdMenu: irdMenuUpdated });
      const resMenu = await apiService.patch(
        `${ENDPOINTS.IRD_MENUS}/${irdMenu.id}`,
        irdMenuBody,
      );
      await Promise.all([
        dispatch(
          getIrdItems({
            revenueCenterId,
            optionTreeKey: IRD_OPTIONS_TREE_KEYS.MENU_BUILDER,
            ...items[IRD_OPTIONS_TREE_KEYS.MENU_BUILDER],
          }),
        ),
        dispatch(
          setIrdMenu({
            irdMenu: {
              ...irdMenuUpdated,
              last_updated: resMenu.data.last_updated,
            },
          }),
        ),
        dispatch(setNodePreviewSelected({ node })),
        dispatch(checkUnpublishedChanges()),
        dispatch(setSavingForm(false)),
        !keepModalOpen && dispatch(setIrdShowModal(false)),
      ]);
      return node;
    } catch (err) {
      console.error(err);
      dispatch(setSavingForm(false));
      return null;
    }
  };

export const validIRDMenuFile = (file) => (dispatch) => {
  const filesProcessed = processFiles({
    fileList: [file],
    mimeTypes: [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
    fileExts: ['.csv, .xls', '.xlsx'],
  });
  if (filesProcessed.rejected.length) {
    dispatch(
      setAlert({
        content:
          'There was an error processing this file. Please select a different file.',
        type: 'warning',
        show: true,
        dismissable: true,
      }),
    );
    return false;
  }
  return true;
};

export const saveIrdMenuCsv =
  ({ file, onCollide }) =>
  async (dispatch, getState) => {
    try {
      const {
        groups: { selectedGroup },
        irdSettings: { irdSettings },
        irdMenu: { items },
      } = getState();
      const fRes = await dispatch(
        uploadFiles({
          files: { accepted: [file], rejected: [] },
        }),
      );
      const fileRes = fRes.accepted[0]; // Uploading one file
      const revenueCenterId = getIdFromUrl({ url: irdSettings.revenue_center });

      await Promise.all([
        saveCSVJob({
          file: fileRes,
          groupId: selectedGroup.id,
          onCollide,
          revenueCenterId,
        }),
        dispatch(
          getIrdItems({
            group: selectedGroup,
            revenueCenterId,
            query: items.query,
            optionTreeKey: IRD_OPTIONS_TREE_KEYS.MENU_BUILDER,
            orderBy: items.orderBy,
            price: items.price,
          }),
        ),
        dispatch(
          setAlert({
            id: ALERTS_IDS.irdCsv,
            title: 'Import File Saved',
            content: 'The import file has been uploaded',
            type: 'success',
            show: true,
            dismissable: true,
          }),
        ),
      ]);
    } catch (error) {
      console.error(error);
      dispatch(
        setAlert({
          id: ALERTS_IDS.irdCsv,
          title: 'Import File Save Failed',
          content: 'There was an error uploading the import file.',
          type: 'danger',
          show: true,
          dismissable: true,
        }),
      );
    }
  };

export const removeIrdNode = (node) => async (dispatch, getState) => {
  try {
    const {
      irdMenu: { irdMenu, items },
      irdSettings: { revenueCenterSelected },
    } = getState();

    const removeNodeFrom = findNodeParent({ menu: irdMenu.menu, node });
    const irdMenuUpdated = removeNodeFromWbTree({
      wbTree: irdMenu,
      node,
      removeNodeFrom,
    });

    const irdMenuBody = getBodyIrdMenu({ irdMenu: irdMenuUpdated });
    const resMenu = await apiService.patch(
      `${ENDPOINTS.IRD_MENUS}/${irdMenu.id}`,
      irdMenuBody,
    );

    const nodePreview = getInicialNode({
      id: removeNodeFrom.id,
      menu: irdMenuUpdated.menu,
    });

    await Promise.all([
      dispatch(
        setIrdMenu({
          irdMenu: {
            ...irdMenuUpdated,
            last_updated: resMenu.data.last_updated,
          },
        }),
      ),
      dispatch(setNodePreviewSelected({ node: nodePreview })),
      dispatch(
        getIrdItems({
          revenueCenterId: revenueCenterSelected.id,
          optionTreeKey: IRD_OPTIONS_TREE_KEYS.OPTION_SET_MODAL,
          ...items[IRD_OPTIONS_TREE_KEYS.OPTION_SET_MODAL],
        }),
      ),
      dispatch(checkUnpublishedChanges()),
    ]);
    return nodePreview;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const canPublishMenu =
  ({ superStrict }) =>
  (dispatch) => {
    const validationResults = dispatch(verifyIrdMenuStructure({ superStrict }));

    let canPublish = true;
    if (validationResults.errors.length) {
      let message =
        'The selected menu cannot be published until the following issues are fixed.<ul>';

      for (const msg of validationResults.errors) {
        message += `<li>${escape(msg)}</li>`;
      }
      message += '</ul>';

      message += addWarnings(validationResults);
      dispatch(
        setAlert({
          content: message,
          type: 'danger',
          show: true,
          dismissable: true,
        }),
      );
      canPublish = false;
    }

    return {
      canPublish,
      validationResults,
    };
  };

export const publishIRD = () => async (dispatch, getState) => {
  try {
    const {
      irdMenu: { irdMenu },
    } = getState();

    const { canPublish, validationResults } = dispatch(
      canPublishMenu({ superStrict: true }),
    );

    if (canPublish) {
      const title = 'Are you sure you want to publish the selected menu?';
      const content = addWarnings(validationResults);

      const isConfirmed = await dispatch(
        initConfirm({
          title,
          content,
        }),
      );
      if (!isConfirmed) {
        return false;
      }

      const timestamp = Math.round(new Date().getTime() / 1000);
      const body = {
        targets: [`${irdMenu.uri}/as_of/${timestamp}`],
        type: 'PUBLISH_IRD_MENU',
      };

      const res = await apiService.post(ENDPOINTS.JOBS, body);
      dispatch(setPublishingStatus({ job: res.data }));
      await dispatch(
        processLatestPublishJob({
          job: res.data,
          irdMenuUri: irdMenu.uri,
        }),
      );
      sendEventGA({ action: GA_ACTIONS.publish, label: GA_LABELS.ird });
    }
  } catch (error) {
    console.error(error);
  }
};

export const patchIrdMenu = (irdMenu) => async (dispatch, getState) => {
  try {
    const {
      irdMenu: { nodePreviewSelected },
    } = getState();
    const bodyIrdMenu = getBodyIrdMenu({
      irdMenu,
    });
    const resMenu = await apiService.patch(
      `${ENDPOINTS.IRD_MENUS}/${irdMenu.id}`,
      bodyIrdMenu,
    );

    const nodePreviewUpdated = findNodeByUri({
      menu: irdMenu.menu,
      nodeUri: nodePreviewSelected.subject.uri,
    });

    await Promise.all([
      dispatch(
        setIrdMenu({
          irdMenu: { ...irdMenu, last_updated: resMenu.data.last_updated },
        }),
      ),
      dispatch(setNodePreviewSelected({ node: nodePreviewUpdated })),
      dispatch(checkUnpublishedChanges()),
    ]);
  } catch (error) {
    console.error(error);
    const {
      irdMenu: { irdMenu: irdMenuOld },
    } = getState();
    dispatch(
      setAlert({
        title: 'Settings save failed',
        content: 'The settings for this property could not be saved.',
        type: 'danger',
        show: true,
        dismissable: true,
      }),
    );
    // Restore previous state
    dispatch(
      setIrdMenu({
        irdMenu: { ...irdMenuOld },
      }),
    );
  }
};
